/**
 * Posture score — client ESM mirror of shared/postureScore.js.
 * Keep aligned: shared/__tests__/postureScore.test.js
 */

export const POSTURE_SCORE_MODEL = "severity_weighted_pass_ratio_v3";

/** Same resolution as shared/severitySummaryCounts.js getFindings. Keep aligned. */
function getFindings(input) {
  if (Array.isArray(input)) return input;
  if (!input || typeof input !== "object") return [];
  const nested = input.assessment;
  if (nested && typeof nested === "object" && Array.isArray(nested.findings)) return nested.findings;
  if (Array.isArray(input.findings)) return input.findings;
  return [];
}

const INCONCLUSIVE_CAUSE = {
  ACCESS_GAP: "access_gap",
  IDWIZ_GAP: "idwiz_gap",
  DATA_ABSENT: "data_absent",
};

function isExcludedFromScore(cause) {
  return cause === INCONCLUSIVE_CAUSE.ACCESS_GAP || cause === INCONCLUSIVE_CAUSE.IDWIZ_GAP;
}

function resolveInconclusiveCause(finding) {
  if (!finding || typeof finding !== "object") return null;
  const ext =
    finding.adapter_extension && typeof finding.adapter_extension === "object"
      ? finding.adapter_extension
      : null;
  const fromExt = ext?.inconclusive_cause;
  if (fromExt === INCONCLUSIVE_CAUSE.ACCESS_GAP || fromExt === INCONCLUSIVE_CAUSE.IDWIZ_GAP || fromExt === INCONCLUSIVE_CAUSE.DATA_ABSENT) {
    return fromExt;
  }
  const direct = finding.inconclusive_cause ?? finding.inconclusiveCause;
  if (direct === INCONCLUSIVE_CAUSE.ACCESS_GAP || direct === INCONCLUSIVE_CAUSE.IDWIZ_GAP || direct === INCONCLUSIVE_CAUSE.DATA_ABSENT) {
    return direct;
  }
  return null;
}

function defaultInconclusiveCause(finding) {
  return resolveInconclusiveCause(finding) || INCONCLUSIVE_CAUSE.DATA_ABSENT;
}

export function buildScoreCoverageLine(counts) {
  const verified = Number(counts?.verified) || 0;
  const unassessed = Number(counts?.unassessed) || 0;
  if (unassessed > 0) {
    return `Score based on ${verified} verified checks; ${unassessed} couldn't be assessed (grant scope / enable).`;
  }
  return `Score based on ${verified} verified checks.`;
}

export const POSTURE_SEVERITY_WEIGHTS = {
  CRITICAL: 5,
  HIGH: 3,
  MEDIUM: 2,
  LOW: 1,
  WARNING: 2,
  INFO: 1,
};

export const POSTURE_CRITICAL_CAP = 70;

export const POSTURE_CAP_POLICY = {
  criticalCap: POSTURE_CRITICAL_CAP,
  perCriticalTiering: null,
  highSeveritySoftCap: null,
};

export function severityWeight(severity, bucket) {
  const raw = String(severity || "").trim();
  if (raw === "healthy" || raw === "inconclusive") return POSTURE_SEVERITY_WEIGHTS.LOW;
  const upper = raw.toUpperCase();
  if (upper === "CRITICAL" || raw === "critical") return POSTURE_SEVERITY_WEIGHTS.CRITICAL;
  if (upper === "HIGH" || raw === "high") return POSTURE_SEVERITY_WEIGHTS.HIGH;
  if (upper === "MEDIUM" || upper === "WARNING" || upper === "WARN" || raw === "warning" || raw === "medium") {
    return POSTURE_SEVERITY_WEIGHTS.MEDIUM;
  }
  if (upper === "LOW" || upper === "INFO" || raw === "low") return POSTURE_SEVERITY_WEIGHTS.LOW;
  if (bucket === "critical") return POSTURE_SEVERITY_WEIGHTS.CRITICAL;
  if (bucket === "warnings") return POSTURE_SEVERITY_WEIGHTS.MEDIUM;
  if (bucket === "healthy") return POSTURE_SEVERITY_WEIGHTS.LOW;
  if (bucket === "inconclusive") return POSTURE_SEVERITY_WEIGHTS.MEDIUM;
  return POSTURE_SEVERITY_WEIGHTS.MEDIUM;
}

function isOpenCriticalFinding(f, bucket) {
  if (!f || typeof f !== "object") return false;
  if (bucket === "critical") return true;
  const status = String(f.status || "").toUpperCase();
  if (status === "NOT_APPLICABLE" || status === "NOT_VISIBLE" || status === "HEALTHY") return false;
  if (status === "INCONCLUSIVE") {
    if (isExcludedFromScore(defaultInconclusiveCause(f))) return false;
  }
  if (status !== "OPEN") return false;
  return String(f.severity || "").toUpperCase() === "CRITICAL";
}

function countOpenCriticalFindings(snap) {
  const normalized = Array.isArray(snap.assessment?.findings) ? snap.assessment.findings : null;
  if (normalized && normalized.length > 0) {
    let count = 0;
    for (const f of normalized) {
      if (isOpenCriticalFinding(f, null)) count += 1;
    }
    return count;
  }
  const findings = snap.findings && typeof snap.findings === "object" ? snap.findings : {};
  return Array.isArray(findings.critical) ? findings.critical.length : 0;
}

function applyScoreCaps(rawScore, ctx) {
  let score = rawScore;
  let cappedBy = null;
  if (ctx.openCriticalCount > 0 && POSTURE_CAP_POLICY.criticalCap != null) {
    const cap = POSTURE_CAP_POLICY.criticalCap;
    if (score > cap) {
      score = cap;
      cappedBy = "critical_cap";
    }
  }
  return { score, cappedBy };
}

export function computePostureScore(snapshot) {
  const snap = snapshot && typeof snapshot === "object" ? snapshot : {};
  const normalized = Array.isArray(snap.assessment?.findings) ? snap.assessment.findings : null;

  let passingWeight = 0;
  let totalWeight = 0;
  let applicableCount = 0;
  let naCount = 0;
  let unassessedCount = 0;

  if (normalized && normalized.length > 0) {
    for (const f of normalized) {
      if (!f || typeof f !== "object") continue;
      const status = String(f.status || "").toUpperCase();
      if (status === "NOT_APPLICABLE" || status === "NOT_VISIBLE") {
        naCount += 1;
        continue;
      }
      if (status === "INCONCLUSIVE" && isExcludedFromScore(defaultInconclusiveCause(f))) {
        unassessedCount += 1;
        continue;
      }
      const weight = severityWeight(f.severity, null);
      totalWeight += weight;
      applicableCount += 1;
      if (status === "HEALTHY") passingWeight += weight;
    }
  } else {
    const findings = snap.findings && typeof snap.findings === "object" ? snap.findings : {};
    const buckets = [
      ["critical", false],
      ["warnings", false],
      ["healthy", true],
      ["inconclusive", false],
      ["other", false],
    ];
    for (const [bucket, isPass] of buckets) {
      const list = Array.isArray(findings[bucket]) ? findings[bucket] : [];
      for (const f of list) {
        if (!f || typeof f !== "object") continue;
        if (bucket === "inconclusive" && isExcludedFromScore(defaultInconclusiveCause(f))) {
          unassessedCount += 1;
          continue;
        }
        const weight = severityWeight(f.severity, bucket);
        totalWeight += weight;
        applicableCount += 1;
        if (isPass) passingWeight += weight;
      }
    }
  }

  const rawScore =
    totalWeight > 0 ? Math.round(Math.max(0, Math.min(100, (passingWeight / totalWeight) * 100))) : 100;

  const openCriticalCount = countOpenCriticalFindings(snap);
  const { score, cappedBy } = applyScoreCaps(rawScore, { openCriticalCount });

  return {
    score,
    rawScore,
    passingWeight,
    totalWeight,
    applicableCount,
    naCount,
    unassessedCount,
    openCriticalCount,
    cappedBy,
    weights: { ...POSTURE_SEVERITY_WEIGHTS },
    model: POSTURE_SCORE_MODEL,
  };
}

function severityDonutSegmentsFromCounts(counts) {
  return [
    { name: "Critical", value: counts.critical, key: "critical" },
    { name: "High", value: counts.high, key: "high" },
    { name: "Medium", value: counts.medium, key: "medium" },
    { name: "Low", value: counts.low, key: "low" },
    { name: "Healthy", value: counts.healthy, key: "healthy" },
    { name: "N/A", value: counts.na, key: "na" },
    { name: "Unassessed", value: counts.unassessed, key: "unassessed" },
  ].filter((d) => d.value > 0);
}

function countSeverityBucketsFromNormalizedFindings(findings) {
  const counts = { critical: 0, high: 0, medium: 0, low: 0, healthy: 0, na: 0, unassessed: 0 };
  for (const f of findings) {
    if (!f || typeof f !== "object") continue;
    const status = String(f.status || "").toUpperCase();
    if (status === "NOT_APPLICABLE" || status === "NOT_VISIBLE") {
      counts.na += 1;
      continue;
    }
    if (status === "INCONCLUSIVE" && isExcludedFromScore(defaultInconclusiveCause(f))) {
      counts.unassessed += 1;
      continue;
    }
    if (status === "HEALTHY") {
      counts.healthy += 1;
      continue;
    }
    const sev = String(f.severity || "").toUpperCase();
    if (sev === "CRITICAL") counts.critical += 1;
    else if (sev === "HIGH") counts.high += 1;
    else if (sev === "MEDIUM" || sev === "WARNING") counts.medium += 1;
    else if (sev === "LOW" || sev === "INFO") counts.low += 1;
    else counts.medium += 1;
  }
  return counts;
}

export function buildSeverityDonut(snapshot) {
  const snap = snapshot && typeof snapshot === "object" ? snapshot : {};
  const summary = snap.summary && typeof snap.summary === "object" ? snap.summary : null;
  if (summary && summary.mediumCount != null) {
    return severityDonutSegmentsFromCounts({
      critical: Number(summary.criticalCount) || 0,
      high: Number(summary.highCount) || 0,
      medium: Number(summary.mediumCount ?? summary.warningCount) || 0,
      low: Number(summary.lowCount ?? summary.infoCount) || 0,
      healthy: Number(summary.healthyCount) || 0,
      na: Number(summary.naCount) || 0,
      unassessed: 0,
    });
  }

  const normalized = Array.isArray(snap.assessment?.findings) ? snap.assessment.findings : null;
  if (normalized && normalized.length > 0) {
    return severityDonutSegmentsFromCounts(countSeverityBucketsFromNormalizedFindings(normalized));
  }

  const f = snap.findings && typeof snap.findings === "object" ? snap.findings : {};
  const critical = Array.isArray(f.critical) ? f.critical.length : 0;
  const warning = Array.isArray(f.warnings) ? f.warnings.length : 0;
  const healthy = Array.isArray(f.healthy) ? f.healthy.length : 0;
  const inconclusive = Array.isArray(f.inconclusive) ? f.inconclusive : [];
  let inconclusiveScored = 0;
  let unassessed = 0;
  for (const row of inconclusive) {
    if (isExcludedFromScore(defaultInconclusiveCause(row))) unassessed += 1;
    else inconclusiveScored += 1;
  }
  return severityDonutSegmentsFromCounts({
    critical,
    high: 0,
    medium: warning + inconclusiveScored,
    low: 0,
    healthy,
    na: 0,
    unassessed,
  });
}

export function buildComplianceByFramework(snapshot, effectiveFrameworks) {
  const snap = snapshot && typeof snapshot === "object" ? snapshot : {};
  const findings = snap.findings && typeof snap.findings === "object" ? snap.findings : {};
  const effective = Array.isArray(effectiveFrameworks) ? effectiveFrameworks : [];
  const effSet = new Set(effective.map(String));

  const buckets = {
    fail: [
      ...(Array.isArray(findings.critical) ? findings.critical : []),
      ...(Array.isArray(findings.warnings) ? findings.warnings : []),
      ...(Array.isArray(findings.other) ? findings.other : []),
    ],
    pass: Array.isArray(findings.healthy) ? findings.healthy : [],
  };

  const counts = new Map();
  for (const fw of effective) counts.set(String(fw), { covered: 0, partial: 0, failing: 0 });

  const controlState = new Map();
  const consider = (finding, kind) => {
    const refs = Array.isArray(finding?.frameworks) ? finding.frameworks : [];
    for (const r of refs) {
      const fw = String(r?.framework || "");
      const ctrl = String(r?.control || "");
      if (!fw || !ctrl || !effSet.has(fw)) continue;
      const key = `${fw}::${ctrl}`;
      const st = controlState.get(key) || { hasFailPrimary: false, hasPass: false };
      if (kind === "pass") st.hasPass = true;
      if (kind === "fail" && String(r?.relevance) === "primary") st.hasFailPrimary = true;
      controlState.set(key, st);
    }
  };

  for (const f of buckets.pass) consider(f, "pass");
  for (const f of buckets.fail) consider(f, "fail");

  for (const [key, st] of controlState.entries()) {
    const [fw] = key.split("::");
    const c = counts.get(fw);
    if (!c) continue;
    if (st.hasFailPrimary) c.failing += 1;
    else if (st.hasPass) c.covered += 1;
    else c.partial += 1;
  }

  return effective.map((fw) => ({
    framework: String(fw),
    ...(counts.get(String(fw)) || { covered: 0, partial: 0, failing: 0 }),
  }));
}

function resolveRiskSurface(ruleId, category) {
  const rid = String(ruleId || "").toUpperCase();
  const cat = String(category || "")
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

  if (
    cat === "ADMINISTRATIVE" ||
    cat === "CONFIGURATION" ||
    cat.includes("ADMIN") ||
    cat.includes("CONFIG") ||
    cat.includes("MONITORING") ||
    cat.includes("NETWORK") ||
    cat.includes("THREAT") ||
    cat.includes("GROUP") ||
    (cat.includes("POLICY") && !cat.includes("SIGN_ON"))
  ) {
    return "Admin & Config";
  }

  if (
    cat === "INTEGRATION" ||
    cat === "AUTHORIZATION" ||
    cat.includes("APP") ||
    cat.includes("OAUTH") ||
    cat.includes("CLIENT") ||
    cat.includes("REDIRECT") ||
    cat.includes("SCOPE") ||
    cat.includes("PKCE") ||
    cat.includes("FEDERATION") ||
    cat.includes("API")
  ) {
    return "Applications";
  }

  if (
    cat === "AUTHENTICATION" ||
    cat === "SESSION" ||
    cat.includes("AUTH") ||
    cat.includes("MFA") ||
    cat.includes("PASSWORD") ||
    cat.includes("CREDENTIAL") ||
    cat.includes("IDENTITY") ||
    cat.includes("SIGN_ON") ||
    cat.includes("AUTHENTICATOR") ||
    cat.includes("LIFECYCLE") ||
    cat.includes("USER")
  ) {
    return "Identity";
  }

  if (rid.includes("-HYDRA-") || rid.includes(".HYDRA.")) return "Applications";
  if (rid.includes("-KRATOS-") || rid.includes(".KRATOS.")) return "Identity";

  if (cat) return "Admin & Config";
  return "Identity";
}

const RISK_SURFACE_ORDER = ["Identity", "Applications", "Admin & Config"];

export function buildRiskBySurface(snapshot) {
  const snap = snapshot && typeof snapshot === "object" ? snapshot : {};
  const normalized = Array.isArray(snap.assessment?.findings) ? snap.assessment.findings : null;
  const weights = new Map();

  const add = (surface, sev) => {
    const w = severityWeight(sev, null);
    weights.set(surface, (weights.get(surface) || 0) + w);
  };

  if (normalized && normalized.length > 0) {
    for (const f of normalized) {
      if (!f || typeof f !== "object") continue;
      const status = String(f.status || "").toUpperCase();
      if (status === "NOT_APPLICABLE" || status === "NOT_VISIBLE" || status === "HEALTHY") continue;
      if (status === "INCONCLUSIVE" && isExcludedFromScore(defaultInconclusiveCause(f))) continue;
      if (status !== "OPEN" && status !== "INCONCLUSIVE") continue;
      add(resolveRiskSurface(f.rule_id || f.ruleId, f.category), f.severity);
    }
  } else {
    const findings = snap.findings && typeof snap.findings === "object" ? snap.findings : {};
    for (const f of [...(findings.critical || []), ...(findings.warnings || [])]) {
      if (!f || typeof f !== "object") continue;
      add(resolveRiskSurface(f.ruleId || f.rule_id, f.category), f.severity);
    }
  }

  return [...weights.entries()]
    .map(([surface, riskWeight]) => ({ surface, riskWeight }))
    .sort((a, b) => {
      const ai = RISK_SURFACE_ORDER.indexOf(a.surface);
      const bi = RISK_SURFACE_ORDER.indexOf(b.surface);
      if (ai !== bi) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      return b.riskWeight - a.riskWeight;
    });
}

export function summarizeFindingDrift(previous, current) {
  const failIds = (snap) => {
    const f = snap?.findings || {};
    const crit = Array.isArray(f.critical) ? f.critical : [];
    const warn = Array.isArray(f.warnings) ? f.warnings : [];
    const out = new Map();
    for (const x of [...crit, ...warn]) {
      const id = String(x?.ruleId || x?.rule_id || "");
      if (!id) continue;
      const sev = String(x?.severity || "").toLowerCase();
      out.set(id, sev === "critical" ? "critical" : "warning");
    }
    return out;
  };

  const passIds = (snap) => {
    const f = snap?.findings || {};
    const healthy = Array.isArray(f.healthy) ? f.healthy : [];
    const out = new Set();
    for (const x of healthy) {
      const id = String(x?.ruleId || x?.rule_id || "");
      if (id) out.add(id);
    }
    return out;
  };

  if (!previous || !current) {
    return { newCritical: 0, newHigh: 0, resolved: 0, items: [] };
  }

  const prevFail = failIds(previous);
  const curFail = failIds(current);
  const prevPass = passIds(previous);

  const items = [];

  for (const [id, sev] of curFail.entries()) {
    if (prevPass.has(id) || !prevFail.has(id)) {
      items.push({ kind: "new", ruleId: id, severity: sev, title: id });
    }
  }

  for (const [id] of prevFail.entries()) {
    if (passIds(current).has(id) || !curFail.has(id)) {
      items.push({ kind: "resolved", ruleId: id, severity: "healthy", title: id });
    }
  }

  return {
    newCritical: items.filter((i) => i.kind === "new" && i.severity === "critical").length,
    newHigh: items.filter((i) => i.kind === "new" && i.severity === "warning").length,
    resolved: items.filter((i) => i.kind === "resolved").length,
    items: items.slice(0, 8),
  };
}

export function scoreColor(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return "var(--text-secondary)";
  if (n > 70) return "var(--healthy)";
  if (n >= 40) return "var(--warning)";
  return "var(--critical)";
}

/** Four-band gauge stroke: crimson < 20 · red 20–40 · amber 40–70 · green > 70 */
export function gaugeScoreColor(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return "var(--text-secondary)";
  if (n > 70) return "var(--healthy)";
  if (n >= 40) return "var(--warning)";
  if (n >= 20) return "var(--critical)";
  return "#9f1239";
}

/**
 * One-line score explanation for exec dashboard (always visible).
 * @param {{ openCriticalCount?: number, score?: number }} posture
 */
export function buildScoreCaption(posture) {
  if (posture?.scoreCoverageLine) return posture.scoreCoverageLine;
  if (posture?.unassessedCount > 0) {
    return buildScoreCoverageLine({
      verified: posture.applicableCount,
      unassessed: posture.unassessedCount,
    });
  }
  const n = Number(posture?.openCriticalCount) || 0;
  if (n > 0) {
    return `severity-weighted · ${n} open critical${n === 1 ? "" : "s"}`;
  }
  const score = Number(posture?.score);
  if (Number.isFinite(score) && score === 0) {
    return "severity-weighted · no findings yet";
  }
  return "severity-weighted";
}

/**
 * Delta vs prior snapshot — list metadata first, full prior body when loaded.
 * @param {number} score
 * @param {unknown[]} snapshots
 * @param {unknown} priorSnapshot
 * @param {string | null | undefined} selectedSnapshotId
 */
export function computeScoreDelta(score, snapshots, priorSnapshot, selectedSnapshotId) {
  if (priorSnapshot) {
    const priorScore = computePostureScore(priorSnapshot).score;
    return { delta: score - priorScore, hasPrior: true };
  }
  const snaps = Array.isArray(snapshots) ? snapshots : [];
  if (snaps.length < 2) return { delta: null, hasPrior: false };
  const activeId = selectedSnapshotId || snaps[0]?.snapshotId;
  const idx = snaps.findIndex((s) => String(s.snapshotId) === String(activeId));
  const priorMeta = idx >= 0 ? snaps[idx + 1] : snaps[1];
  const priorStored = priorMeta?.summary?.postureScore;
  if (typeof priorStored === "number") {
    return { delta: score - priorStored, hasPrior: true };
  }
  return { delta: null, hasPrior: false };
}

function formatTrendDayKey(takenAt) {
  try {
    return new Date(takenAt).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return String(takenAt || "").slice(0, 10) || "—";
  }
}

function formatTrendTime(takenAt) {
  try {
    return new Date(takenAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

export function buildTrendSeries(snapshots, auditData) {
  const snaps = Array.isArray(snapshots) ? snapshots : [];
  const raw = snaps
    .slice()
    .reverse()
    .map((s) => {
      const stored = s?.summary?.postureScore;
      const score = typeof stored === "number" ? stored : computePostureScore(s).score;
      const takenAt = s.takenAt;
      const dayKey = formatTrendDayKey(takenAt);
      return { takenAt, dayKey, score, snapshotId: s.snapshotId };
    });

  const dayCounts = new Map();
  for (const p of raw) {
    dayCounts.set(p.dayKey, (dayCounts.get(p.dayKey) || 0) + 1);
  }

  const series = raw.map((p) => {
    const multiIntraday = (dayCounts.get(p.dayKey) || 0) > 1;
    const date = multiIntraday ? `${p.dayKey} ${formatTrendTime(p.takenAt)}`.trim() : p.dayKey;
    return { date, score: Number.isFinite(p.score) ? p.score : 0, snapshotId: p.snapshotId };
  });

  if (series.length === 0 && auditData) {
    series.push({ date: "Now", score: computePostureScore(auditData).score, snapshotId: null });
  }
  return series;
}

/** Full severity counts for legend (includes zero buckets). */
export function buildSeverityCounts(snapshot) {
  const segments = buildSeverityDonut(snapshot);
  const counts = { critical: 0, high: 0, medium: 0, low: 0, warning: 0, healthy: 0, na: 0, unassessed: 0 };
  for (const seg of segments) {
    if (seg.key in counts) counts[seg.key] = seg.value;
  }
  // Re-count from source when segments are filtered (donut omits zeros)
  const snap = snapshot && typeof snapshot === "object" ? snapshot : {};
  const summary = snap.summary && typeof snap.summary === "object" ? snap.summary : null;
  const normalized = getFindings(snap);
  const summaryLooksEmpty =
    !summary ||
    !(
      Number(summary.criticalCount) ||
      Number(summary.highCount) ||
      Number(summary.mediumCount) ||
      Number(summary.warningCount) ||
      Number(summary.lowCount) ||
      Number(summary.healthyCount) ||
      Number(summary.inconclusiveCount)
    );
  if (summary && summary.mediumCount != null && !(summaryLooksEmpty && normalized.length > 0)) {
    counts.critical = Number(summary.criticalCount) || 0;
    counts.high = Number(summary.highCount) || 0;
    counts.medium = Number(summary.mediumCount ?? summary.warningCount) || 0;
    counts.low = Number(summary.lowCount ?? summary.infoCount) || 0;
    counts.warning = counts.medium;
    counts.healthy = Number(summary.healthyCount) || 0;
    counts.na = Number(summary.naCount) || 0;
    counts.unassessed = 0;
    const total =
      counts.critical + counts.high + counts.medium + counts.low + counts.healthy + counts.na + counts.unassessed;
    return { counts, total, segments: buildSeverityDonut(snapshot) };
  }
  if (normalized.length > 0) {
    counts.critical = 0;
    counts.high = 0;
    counts.medium = 0;
    counts.low = 0;
    counts.warning = 0;
    counts.healthy = 0;
    counts.na = 0;
    counts.unassessed = 0;
    for (const f of normalized) {
      if (!f || typeof f !== "object") continue;
      const status = String(f.status || "").toUpperCase();
      if (status === "NOT_APPLICABLE" || status === "NOT_VISIBLE") {
        counts.na += 1;
        continue;
      }
      if (status === "INCONCLUSIVE" && isExcludedFromScore(defaultInconclusiveCause(f))) {
        counts.unassessed += 1;
        continue;
      }
      if (status === "HEALTHY") {
        counts.healthy += 1;
        continue;
      }
      const sev = String(f.severity || "").toUpperCase();
      if (sev === "CRITICAL") counts.critical += 1;
      else if (sev === "HIGH") counts.high += 1;
      else if (sev === "MEDIUM" || sev === "WARNING") counts.medium += 1;
      else if (sev === "LOW" || sev === "INFO") counts.low += 1;
      else counts.medium += 1;
    }
    counts.warning = counts.medium;
  } else {
    const f = snap.findings && typeof snap.findings === "object" ? snap.findings : {};
    counts.critical = Array.isArray(f.critical) ? f.critical.length : 0;
    counts.warning = Array.isArray(f.warnings) ? f.warnings.length : 0;
    counts.healthy = Array.isArray(f.healthy) ? f.healthy.length : 0;
    counts.na = 0;
    counts.unassessed = 0;
    for (const row of Array.isArray(f.inconclusive) ? f.inconclusive : []) {
      if (isExcludedFromScore(defaultInconclusiveCause(row))) counts.unassessed += 1;
      else counts.warning += 1;
    }
  }
  const total = counts.critical + counts.high + counts.medium + counts.low + counts.healthy + counts.na + counts.unassessed;
  return { counts, total, segments: buildSeverityDonut(snapshot) };
}
